<template>
  <header class="header">
    <div
      class="header__container"
      :class="{ active: isPossibilitiesDropDownOpen }"
    >
      <nav>
        <ul class="header__links">
          <li class="header__item">
            <BaseLink
              class="header__logo-wrapper"
              :href="`/${locale}`"
            >
              <img
                alt=""
                class="header__logo"
                src="/logos/logo.svg"
              />
              MyDonut
            </BaseLink>
          </li>

          <li class="header__item">
            <BaseDropdown2
              id="clientPossibilitiesDropdown"
              ref="clientPossibilitiesDropdown"
              :offset-x="-1400"
              @close="() => (isPossibilitiesDropDownOpen = false)"
              @open="() => (isPossibilitiesDropDownOpen = true)"
            >
              <template #reference>
                <BaseButton2
                  class="header__link"
                  :class="{ active: isPossibilitiesDropDownOpen }"
                >
                  <span v-html="t('header.possibilities')" />

                  <BaseIcon2
                    id="icon-cap"
                    class="header__link-caret"
                    :class="{ active: isPossibilitiesDropDownOpen }"
                    height="16"
                    width="16"
                  />
                </BaseButton2>
              </template>

              <template #content>
                <ul class="dropdown__list">
                  <li
                    v-for="(accordion, accordionIndex) in accordionList"
                    :key="accordionIndex"
                  >
                    <BaseButton2
                      class="dropdown__item"
                      @click="() => closeDropdownList(accordion.href)"
                    >
                      <h4
                        class="typography__title3"
                        v-html="accordion.name"
                      />

                      <p
                        class="typography__label1"
                        v-html="accordion.description"
                      />
                    </BaseButton2>
                  </li>
                </ul>
              </template>
            </BaseDropdown2>
          </li>

          <li
            v-for="(link, linkIndex) in menu"
            :key="linkIndex"
            class="header__item"
          >
            <BaseLink
              class="header__link"
              :href="link.href"
            >
              <span v-html="link.name" />
            </BaseLink>
          </li>
        </ul>
      </nav>

      <div class="header__tools-container">
        <TheHeaderTools />

        <BaseButton2
          class="header__burger"
          @click="() => openMenu()"
        >
          <BaseIcon2
            id="icon-burger"
            height="24"
            width="24"
          />
        </BaseButton2>
      </div>
    </div>

    <TheMenuMobile
      :accordion-list
      class="ui-scrollbar-primary"
      :menu
    />
  </header>
</template>

<script setup lang="ts">
const { t, locale } = useI18n();

const accordionList = computed(() => [
  {
    description: t("header.accordions.onlineRecord.description"),
    href: "/" + locale.value + "/features/online-record",
    name: t("header.accordions.onlineRecord.name"),
  },
  {
    description: t("header.accordions.shop.description"),
    href: "/" + locale.value + "/features/shop",
    name: t("header.accordions.shop.name"),
  },
  {
    description: t("header.accordions.applications.description"),
    href: "/" + locale.value + "/features/services-with-applications",
    name: t("header.accordions.applications.name"),
  },
  {
    description: t("header.accordions.crm.description"),
    href: "/" + locale.value + "/features/crm",
    name: t("header.accordions.crm.name"),
  },
  {
    description: t("header.accordions.chats.description"),
    href: "/" + locale.value + "/features/chats",
    name: t("header.accordions.chats.name"),
  },
  {
    description: t("header.accordions.website.description"),
    href: "/" + locale.value + "/features/website",
    name: t("header.accordions.website.name"),
  },
  {
    description: t("header.accordions.bot.description"),
    href: "/" + locale.value + "/features/bot",
    name: t("header.accordions.bot.name"),
  },
  {
    description: t("header.accordions.cabinet.description"),
    href: "/" + locale.value + "/features/client-cabinet",
    name: t("header.accordions.cabinet.name"),
  },
]);

const menu = computed(() => [
  {
    href: "/" + locale.value + "/#examples",
    name: t("header.menu.examples"),
  },
  {
    href: "/" + locale.value + "/rates",
    name: t("header.menu.rates"),
  },
  {
    href: "/" + locale.value + "/questions",
    name: t("header.menu.questions"),
  },
]);

const { toggleMenu } = useAppStore();

const openMenu = () => {
  toggleMenu(true);
  pageScrollBlock();
};

const clientPossibilitiesDropdown = ref<HTMLElement[] | null>(null);
const isPossibilitiesDropDownOpen = ref(false);

const closeDropdownList = async (href: string) => {
  if (clientPossibilitiesDropdown.value) {
    clientPossibilitiesDropdown.value.closeDropdown();
  }

  await navigateTo(href);
};
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 2;
  padding-top: 16px;
}

.header__container {
  position: relative;
  z-index: var(--z-index-header);
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1286px;
  height: 48px;
  margin: 0 auto;
  padding: 4px 12px 4px 24px;
  border-radius: 50px;
  background-color: var(--white);
  box-shadow: 0 3px 16px 0 rgb(0 0 0 / 8%);
}

@media (--desktop) {
  .header__container {
    min-width: 1036px;
    height: 68px;
    padding: 0 40px;
  }

  .header__container.active {
    border-radius: 34px 34px 0 0;
  }
}

@media (--desktop) {
  .header__logo {
    width: 104px;
    height: 20px;
  }
}

.header__links,
.header__item {
  display: flex;
  align-items: center;
}

.header__link {
  display: none;
}

@media (--desktop) {
  .header__link {
    display: flex;
    align-items: center;
    column-gap: 4px;
    padding: 8px 24px;
  }

  .header__link.active {
    color: var(--orange-3);
  }
}

.header__link-caret {
  rotate: 90deg;
  color: var(--grey-3);
}

.header__link-caret.active {
  rotate: -90deg;
}

@media (--hover) {
  .header__link:hover,
  .header__link:active {
    color: var(--orange-3);
  }
}

.dropdown__list {
  position: relative;
  top: 15px;
  left: -170px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 16px;
  max-width: 1286px;
  min-width: 1036px;
  padding: 0 24px 16px;
  border-radius: 0 0 34px 34px;
  background-color: var(--white);
  box-shadow: 0 10px 16px 0 rgb(0 0 0 / 8%);
}

.dropdown__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 8px;
  padding: 16px;
  text-align: left;
  border-radius: 16px;
}

@media (--hover) {
  .dropdown__item:hover,
  .dropdown__item:active {
    background-color: var(--grey-7);
  }
}

.header__tools-container {
  display: flex;
  align-items: center;
  column-gap: 12px;
}

.header__burger {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  color: var(--grey-3);
}

@media (--desktop) {
  .header__burger {
    display: none;
  }
}

.header__logo-wrapper {
  font-family:
    Plus Jakarta Sans,
    Inter,
    system-ui,
    -apple-system,
    sans-serif;
  font-size: 16px;
  font-weight: 700;
  gap: 8px;
}

.header__logo {
  justify-content: center;
  height: 25px;
  width: 25px;
}

@media (--desktop) {
  .header__logo-wrapper {
    font-size: 20px;
    font-weight: 700;
    gap: 12px;
  }

  .header__logo {
    height: 40px;
    width: 40px;
  }
}
</style>
